import { Add } from '@carbon/icons-react';
import * as React from 'react';

import { SelectionCard } from 'src/components/dataSourceSelection/SelectionCard';
import { DataSourceIcon } from 'src/components/resourceDialog/DataSourceIcon';
import { DataSourceOption } from 'src/constants/dataSourceTypeDescription';
import { getDataSourceCardTestId } from 'src/constants/testIds';
import { DataSource } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import { useDesignerPermissions } from 'src/utils/hooks/useDesignerPermissions';

export type ConnectionCardProps = {
  dataSource: DataSource | null;
  dataSourceType: string;
  isNewConnection?: boolean;
  onSelect: () => void;
};

export const ConnectionCard: React.VFC<ConnectionCardProps> = ({
  dataSource,
  dataSourceType,
  isNewConnection = false,
  onSelect,
}) => {
  const { canCreateDataSource, canReadDataSource } = useDesignerPermissions();

  const label = isNewConnection
    ? copy.components.virtualGraphPreviewDialog.connectionSelection
        .newConnection
    : dataSource?.id || '';

  const endpoint = dataSource?.options?.[DataSourceOption.ENDPOINT];

  const icon = isNewConnection ? (
    <Add size={48} />
  ) : (
    <DataSourceIcon
      altText={`${dataSourceType} icon`}
      dataSourceType={dataSourceType}
    />
  );

  // Use dataSource.id as the test ID for existing connections
  const testId = dataSource?.id
    ? getDataSourceCardTestId(dataSource.id)
    : undefined;

  let disabled;
  let disabledTooltip;
  if (isNewConnection && !canCreateDataSource) {
    // Disable "New Connection" cards when user lacks permission
    disabled = true;
    disabledTooltip =
      copy.components.dialogConnectionTile
        .insufficientPrivilegesCreateDataSource;
  } else if (!isNewConnection && !canReadDataSource) {
    // Disable existing connection cards when user lacks permission
    disabled = true;
    disabledTooltip =
      copy.components.dialogConnectionTile.insufficientPrivilegesReadDataSource;
  }

  return (
    <SelectionCard
      disabled={disabled}
      disabledTooltip={disabled ? disabledTooltip : undefined}
      icon={icon}
      label={label}
      onSelect={onSelect}
      testId={testId}
      tooltipText={endpoint}
      variant={isNewConnection ? 'new' : 'default'}
    />
  );
};
